import React, { useMemo } from 'react';
import { Box, Inscription, List } from '@common-fe/common-fe';
import _ from 'lodash';
import styled, { css } from 'styled-components';

import { INFINITE_DATE } from '@/common/constants';
import { IAccountStatusDtoStatusEnum } from '@/types';

import { checkIsInRange } from '../EnrollmentStatusesTable';

import { EnrollmentAccountHistoryPayload } from './EnrollmentsAccountHistory.types';
import EnrollmentsAccountHistoryStatus from './EnrollmentsAccountHistoryStatus';

interface Props extends EnrollmentAccountHistoryPayload {
  isFirstItem?: boolean;
}

interface WrapperProps {
  $outlineColor: string;
}
const Wrapper = styled(Box)<WrapperProps>`
  ${({ theme, $outlineColor }) => {
    if ($outlineColor) {
      return css`
        border-color: ${_.get(theme.colors, $outlineColor)};
        outline: 1px solid ${_.get(theme.colors, $outlineColor)};
      `;
    }

    return css``;
  }}
`;
const EnrollmentsAccountHistory: React.FC<Props> = ({ status, from, to, isFirstItem }) => {
  const borderColor = useMemo(() => {
    if (!isFirstItem) {
      return '';
    }
    if (status === IAccountStatusDtoStatusEnum.OPEN && checkIsInRange(from, to)) {
      return 'successBorder';
    } else if (status === IAccountStatusDtoStatusEnum.CLOSED && checkIsInRange(from, to)) {
      return 'dangerBorder';
    }
    return '';
  }, [from, isFirstItem, status, to]);

  const isToForever = useMemo(() => {
    if (to === INFINITE_DATE) {
      return true;
    }
    return false;
  }, [to]);
  return (
    <Wrapper
      pad={{ vertical: 'spacing8', horizontal: 'spacing12' }}
      style={{ minHeight: '34px' }}
      background="canvas"
      direction="row"
      border={{ size: '1px', color: borderColor || 'border2' }}
      round="snackbarRound"
      $outlineColor={borderColor}
      margin={{ top: 'spacing4' }}
      data-testid={`enrollments-account-history_item-${status.toLowerCase()}`}
    >
      <Box direction="row" style={{ flex: 1 }} justify="start">
        <EnrollmentsAccountHistoryStatus
          isWithIndicator={isFirstItem}
          status={status}
          data-testid={`enrollments-account-history_status-${status.toLowerCase()}`}
        />

        {isFirstItem && (
          <Box
            height="18px"
            background="border1"
            pad={{ horizontal: 'xxs' }}
            margin={{ left: 'xs' }}
            round="button2Round"
            data-testid={`enrollments-account-history_item-${status.toLowerCase()}_is-current`}
          >
            <Inscription size="10px" weight={500} lineHeight="18px">
              Current
            </Inscription>
          </Box>
        )}
      </Box>

      <List.Item.Text
        size="small"
        weight={500}
        data-testid={`enrollments-account-history_item-${status.toLowerCase()}_period`}
      >
        <Inscription
          size="small"
          weight={500}
          data-testid={`enrollments-account-history_item-${status.toLowerCase()}_period-start`}
        >
          {from}
        </Inscription>
        {to && !isToForever ? ' - ' : ''}
        {isToForever ? (
          ''
        ) : (
          <Inscription
            size="small"
            weight={500}
            data-testid={`enrollments-account-history_item-${status.toLowerCase()}_period-end`}
          >
            {to}
          </Inscription>
        )}
      </List.Item.Text>
    </Wrapper>
  );
};

export default EnrollmentsAccountHistory;
